import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeviceCreate } from '../../../api/models';
import { ManagementOauthApi } from '../../../api/apis';
import { apiClient } from '../../../services/apiClient';
import { clientService } from '../../../services/clientService';
import { useI18n } from '../../../contexts/I18nContext';
import { ClientWithDevices, PaginationParams } from '../../../types/client';
import { agenciesOf, isAgency } from '../../../utils/clientTree';

export type ClientDetailTab = 'overview' | 'devices' | 'users' | 'history';

/**
 * Everything the client dialog shows and does: the identity form (name,
 * SIREN, status, application access), the paged list of its devices, and
 * the messages.
 */
export const useClientDetail = (client: ClientWithDevices | null, isOpen: boolean, onClientUpdated?: () => void, siblings: ClientWithDevices[] = []) => {
  const { t } = useI18n();
  const managementApi = useMemo(() => new ManagementOauthApi(apiClient.getConfiguration()), []);

  const [activeTab, setActiveTab] = useState<ClientDetailTab>('overview');
  const [error, setError] = useState<string | null>(null);

  // Identity form
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedSiren, setEditedSiren] = useState('');
  const [editedActive, setEditedActive] = useState(true);
  const [editedAccess, setEditedAccess] = useState(true);
  const [editedNic, setEditedNic] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Agencies (docs/agences.md in the backend): the root above, the agencies beneath.
  const root = useMemo(() => (client?.parent_id ? siblings.find((s) => s.id === client.parent_id) ?? null : null), [client, siblings]);
  const agencies = useMemo(() => (client ? (agenciesOf(client.id, siblings) as ClientWithDevices[]) : []), [client, siblings]);
  const clientIsAgency = client ? isAgency(client) : false;

  // Devices tab
  const [devices, setDevices] = useState<DeviceCreate[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationParams>({ page: 1, pageSize: 10 });
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const seedForm = useCallback((c: ClientWithDevices) => {
    setEditedName(c.name);
    setEditedSiren(c.siren || '');
    setEditedActive(c.active ?? true);
    setEditedAccess(c.provisioning_access ?? true);
    setEditedNic(c.nic || '');
  }, []);

  useEffect(() => {
    if (!client) return;
    seedForm(client);
    setError(null);
  }, [client, seedForm]);

  useEffect(() => {
    if (isOpen) return;
    setActiveTab('overview');
    setIsEditing(false);
    setError(null);
  }, [isOpen]);

  const fetchDevices = useCallback(async () => {
    if (!client?.id) return;
    try {
      setDevicesLoading(true);
      const response = await managementApi.apiGetConfByClientV2V2ConfClientClientIdGet({
        clientId: client.id,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      if (response && typeof response === 'object' && 'items' in response) {
        // This endpoint returns the count as `total` (flat), unlike /v2/conf
        // which nests it as pagination.total_items: accept both.
        setDevices(response.items || []);
        setTotalDevices(response.total ?? response.total_items ?? 0);
        setTotalPages(response.total_pages || 1);
      } else if (Array.isArray(response)) {
        const start = (pagination.page - 1) * pagination.pageSize;
        setDevices(response.slice(start, start + pagination.pageSize));
        setTotalDevices(response.length);
        setTotalPages(Math.ceil(response.length / pagination.pageSize));
      } else {
        setDevices([]);
        setTotalDevices(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setDevices([]);
      setTotalDevices(0);
      setTotalPages(0);
    } finally {
      setDevicesLoading(false);
    }
  }, [client, managementApi, pagination.page, pagination.pageSize]);

  // The devices load when their tab opens, and again on each page change.
  useEffect(() => {
    if (isOpen && client && activeTab === 'devices') fetchDevices();
  }, [isOpen, client, activeTab, fetchDevices]);

  const sirenValid = editedSiren === '' || /^\d{9}$/.test(editedSiren);
  const nicValid = editedNic === '' || /^\d{5}$/.test(editedNic);
  const canSave = Boolean(editedName.trim()) && sirenValid && nicValid && !isSaving;

  const save = async () => {
    if (!client || !canSave) return;
    try {
      setIsSaving(true);
      setError(null);
      // A plain PATCH: the generated client model does not know provisioning_access and would drop it.
      await apiClient.getAxiosInstance().patch(`/v2/client/${client.id}`, {
        name: editedName.trim(), siren: editedSiren.trim() || null, active: editedActive, provisioning_access: editedAccess,
        ...(clientIsAgency ? { nic: editedNic.trim() || null } : {}),
      });
      clientService.clearCache();
      setIsEditing(false);
      onClientUpdated?.();
    } catch (err: any) {
      console.error('[CLIENT DETAIL] Failed to update client:', err);
      setError(err?.response?.data?.detail || err?.message || t('clients.detail.updateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEdit = () => {
    if (client) seedForm(client);
    setIsEditing(false);
    setError(null);
  };

  const goToPage = (page: number) => setPagination((prev) => ({ ...prev, page }));

  return {
    client, activeTab, setActiveTab, error,
    isEditing, setIsEditing, editedName, setEditedName, editedSiren, setEditedSiren, editedActive, setEditedActive, editedAccess, setEditedAccess,
    sirenValid, canSave, isSaving, save, cancelEdit,
    editedNic, setEditedNic, nicValid, root, agencies, clientIsAgency,
    devices, devicesLoading, pagination, totalDevices, totalPages, goToPage, fetchDevices,
  };
};

export type ClientDetailState = ReturnType<typeof useClientDetail>;
